import moment from 'moment';
import { Reducer } from 'redux';
import { action, ActionType } from 'typesafe-actions';
import { CompositeSite, InjectedSiteStatus, ThunkResult } from '../../typing/types';
import { OrganizationSchedule, Site } from '../../utils/classes';

export const FILTER_SITES = '@site/FILTER_SITES';
export const SET_SCHEDULED_SITES = '@site/SET_SCHEDULED_SITES';
export const SET_IS_FETCHING_MY_SITES = '@site/SET_IS_FETCHING_MY_SITES';
export const SET_SCHEDULE_FETCH_ERROR = '@site/SET_SCHEDULE_FETCH_ERROR';
export const SET_SCHEDULE_ITEMS = '@site/SET_SCHEDULE_ITEMS';
import { logErrorWithContext } from '../../utils/sentry/sentrySetup';
import { NIMBLY_API_SCHEDULES_LITE_V2 } from '../../constants/api';
import type { ScheduleApiFilterParams } from '../../typing/scheduleFilter';
import API from '../helpers/api';
import { mapActiveScheduleToScheduledSites, mapActiveScheduleToScheduleItems } from '../../utils/scheduleMapper';
import type { ScheduleListItemData } from '../../typing/scheduleItem';
/**
 * True when the async function for sites fetching is running. False for otherwise.
 * @param bool
 */
const setIsFetchingMySites = (bool: boolean) => action(SET_IS_FETCHING_MY_SITES, { bool });

const setScheduleFetchError = (hasError: boolean) => action(SET_SCHEDULE_FETCH_ERROR, { hasError });

type MySites = { [siteKey: string]: CompositeSite };

/**
 * (Unused functions)
 * @param mySites
 * @param organizationSchedule
 */
const filterSites = (mySites: MySites, organizationSchedule: OrganizationSchedule) => {
  if (!mySites) {
    return action(FILTER_SITES, { list: [], data: {} });
  }

  const filteredSitesList: { key: string; value: CompositeSite }[] = [];
  const filteredSitesData: { [siteKey: string]: CompositeSite } = {};
  for (const siteKey of Object.keys(mySites)) {
    const currentSite: CompositeSite = mySites[siteKey];
    if ((!currentSite.siteSchedules && !currentSite.schedules) || currentSite.disabled) {
      continue;
    }
    if (currentSite.siteSchedules) {
      for (const date of Object.keys(currentSite.siteSchedules)) {
        if (date === 'assignedAuditor') {
          continue;
        }
        const isExists = Number(currentSite.siteSchedules[date]) > 0;
        if (isExists) {
          filteredSitesList.push({ key: siteKey, value: currentSite });
          filteredSitesData[siteKey] = currentSite;
          break;
        }
      }
    } else if (currentSite.schedules) {
      for (const date of Object.keys(currentSite.schedules)) {
        if (date === 'assignedAuditor') {
          continue;
        }
        const isExists = Number(currentSite.schedules[date]) > 0;
        if (isExists) {
          filteredSitesList.push({ key: siteKey, value: currentSite });
          filteredSitesData[siteKey] = currentSite;
          break;
        }
      }
    }
  }

  return action(FILTER_SITES, { list: filteredSitesList, data: filteredSitesData });
};

/**
 * Setter for scheduledSites
 * @param scheduledSites array of ScheduledSiteInfo
 */
const setMyScheduledSites = (
  scheduledSites: ScheduledSiteInfo[],
  now: number = Date.now(),
  etag: string | null = null,
  range?: { startDate: string; endDate: string }
) => action(SET_SCHEDULED_SITES, { scheduledSites, now, etag, ...range });

const setScheduleItems = (scheduleItems: ScheduleListItemData[], totalPercentage: number) =>
  action(SET_SCHEDULE_ITEMS, { scheduleItems, totalPercentage });

/**
 * Retrieve scheduled sites from firebase
 * @param organizationSchedule
 */

const getMyScheduledSites =
  (organizationSchedule: OrganizationSchedule, selectedDate?: string, filterParams?: ScheduleApiFilterParams): ThunkResult =>
  async (dispatch, getState) => {
    const state = getState();
    const { isConnected } = state.network;

    if (!isConnected) {
      return;
    }

    // const serverTime = moment(getServerTime());
    const serverTime = moment();
    const now = serverTime.valueOf();
    const targetDate =
      selectedDate && moment(selectedDate, 'YYYY-MM-DD', true).isValid()
        ? moment(selectedDate, 'YYYY-MM-DD')
        : moment(now);

    const startDate = targetDate.format('YYYY-MM-DD');
    const endDate = startDate;

    dispatch(setIsFetchingMySites(true));

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams({ startDate, endDate, timezone, forOfflineSync: 'true' });
      if (filterParams?.sites?.length) {
        filterParams.sites.forEach(s => params.append('sites', s));
      }
      if (filterParams?.status?.length) {
        // API expects lowercase-kebab: not-started, in-progress, completed, overdue
        filterParams.status.forEach(s => params.append('status', s.toLowerCase().replace(/_/g, '-')));
      }
      if (filterParams?.types?.length) {
        // API expects lowercase: scheduled, adhoc
        filterParams.types.forEach(t => params.append('types', t.toLowerCase()));
      }
      if (filterParams?.scheduleType?.length) {
        filterParams.scheduleType.forEach(s => params.append('scheduleType', s));
      }
      const api = await API.withToken();
      const res = await api.getJSON(`${NIMBLY_API_SCHEDULES_LITE_V2}?${params.toString()}`);

      if (res?.data) {
        const scheduledSites = mapActiveScheduleToScheduledSites(res.data);
        dispatch(setMyScheduledSites(scheduledSites, now, null, { startDate, endDate }));

        const scheduleItems = mapActiveScheduleToScheduleItems(res.data);
        dispatch(setScheduleItems(scheduleItems, res.data.totalPercentage ?? 0));
      } else {
        logErrorWithContext(
          new Error('Schedule API returned null or missing data'),
          'schedule_lite_v2_empty_response',
          { startDate, endDate, responseIsNull: res === null }
        );
        dispatch(setScheduleFetchError(true));
      }
    } catch (err) {
      logErrorWithContext(err instanceof Error ? err : new Error(String(err)), 'schedule_lite_v2_fetch_error', {
        startDate,
        endDate
      });
      dispatch(setIsFetchingMySites(false));
      dispatch(setScheduleFetchError(true));
      return;
    } finally {
      dispatch(setIsFetchingMySites(false));
    }
  };

export const scheduleSitesActions = {
  filterSites,
  setMyScheduledSites,
  setIsFetchingMySites,
  setScheduleFetchError,
  setScheduleItems
};

export const scheduleSitesThunk = {
  getMyScheduledSites
};

export interface ScheduledSiteInfo {
  isScheduledThisPeriod: boolean;
  scheduledCount: number;
  completedCount: number;
  siteProfile: Site;
  siteKey: string;
  isDraft?: boolean;
  downloadStatus: string;
  isCompletedThisPeriod?: boolean;
  isCompletedToday?: boolean;
  isMakeUp?: boolean;
  isDueToday?: boolean;
  isPendingUpload?: boolean;
  status?: InjectedSiteStatus;
  skuScheduledCount: number;
  skuCompletedCount: number;
  isSKUDraft?: boolean;
  isSKUDueToday?: boolean;
  isSKUScheduledThisPeriod?: boolean;
  isSKUCompletedThisPeriod?: boolean;
  isSKUCompletedToday?: boolean;
  yesterdayActiveScheduleCount?: number;
  yesterdayActiveCompleteCount?: number;
}

export interface ScheduledSites {
  etag: string | null;
  sites: ScheduledSiteInfo[];
}

export type ScheduleSitesAction = ActionType<typeof scheduleSitesActions>;

export type ScheduleSitesState = {
  readonly filteredSitesList: { key: string; value: CompositeSite }[];
  readonly filteredSitesData: { [siteKey: string]: CompositeSite };
  readonly sitesFiltered: boolean;
  readonly myScheduledSites: ScheduledSiteInfo[];
  readonly isFetchingMySites: boolean;
  readonly hasFetchError: boolean;
  readonly timestamp: null | number;
  readonly lastFetchedStartDate: string | null;
  readonly lastFetchedEndDate: string | null;
  readonly etag: string | null;
  readonly myScheduleItems: ScheduleListItemData[];
  readonly totalPercentage: number;
};

const initialState: ScheduleSitesState = {
  filteredSitesList: [],
  filteredSitesData: {},
  sitesFiltered: false,
  myScheduledSites: [],
  isFetchingMySites: false,
  hasFetchError: false,
  timestamp: null,
  lastFetchedStartDate: null,
  lastFetchedEndDate: null,
  etag: null,
  myScheduleItems: [],
  totalPercentage: 0
};

export const scheduleSitesReducer: Reducer<ScheduleSitesState, ScheduleSitesAction> = (
  state = initialState,
  incomingAction
) => {
  switch (incomingAction.type) {
    case FILTER_SITES:
      return {
        ...state,
        sitesFiltered: true,
        filteredSitesList: incomingAction.payload.list,
        filteredSitesData: incomingAction.payload.data
      };
    case SET_SCHEDULED_SITES:
      return {
        ...state,
        myScheduledSites: incomingAction.payload.scheduledSites,
        timestamp: incomingAction.payload.now,
        lastFetchedStartDate: incomingAction.payload.startDate ?? state.lastFetchedStartDate,
        lastFetchedEndDate: incomingAction.payload.endDate ?? state.lastFetchedEndDate,
        etag: incomingAction.payload.etag
      };
    case SET_IS_FETCHING_MY_SITES:
      return {
        ...state,
        isFetchingMySites: incomingAction.payload.bool,
        // clear error when a new fetch begins
        ...(incomingAction.payload.bool ? { hasFetchError: false } : {})
      };
    case SET_SCHEDULE_FETCH_ERROR:
      return {
        ...state,
        hasFetchError: incomingAction.payload.hasError,
        isFetchingMySites: false
      };
    case SET_SCHEDULE_ITEMS:
      return {
        ...state,
        myScheduleItems: incomingAction.payload.scheduleItems,
        totalPercentage: incomingAction.payload.totalPercentage
      };
    default:
      return state;
  }
};
